import React, { useEffect } from 'react'; // Import React and hooks for animation control
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion'; // Import motion
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'; // Import logout icon
import { useAuthStore } from '../../store/authStore'; // Import auth store
import { useLayoutAnimation } from '../../utils/layoutAnimationManager'; // Import animation manager
import NotificationBell from '../notifications/NotificationBell'; // Import notification bell

// Define the props interface
interface NavbarProps {
  toggleSidebar: () => void;
}

// Helper function to determine the highest subscription plan
const getSubscriptionPlan = (roles: string[] | undefined): string | null => {
  if (!roles) return null;
  if (roles.includes('elite-subscriber')) return 'Elite';
  if (roles.includes('standard-subscriber')) return 'Standard';
  if (roles.includes('basic-subscriber')) return 'Basic';
  if (roles.includes('trial-subscriber')) return 'Trial';
  return null;
};

const Navbar = ({ toggleSidebar }: NavbarProps) => { // Accept toggleSidebar prop
  const { user } = useAuth0();
  const { userBillingName, clearUserData, logoutAuth0 } = useAuthStore();
  
  // Use layout animation manager instead of local state
  const { hasCompleted: hasAnimated, markComplete } = useLayoutAnimation();
  
  // Mark animations complete after initial animation
  useEffect(() => {
    if (!hasAnimated) {
      // Auto-complete after a short delay to not block initial load
      const timer = setTimeout(() => markComplete(), 600);
      return () => clearTimeout(timer);
    }
  }, [hasAnimated, markComplete]);

  // Get roles from the custom claim for subscription plan display
  const userRoles = user?.[`https://tradingdashboard.com/roles`] as string[] | undefined;
  const plan = getSubscriptionPlan(userRoles);

  // Handle logout with cleanup
  const handleLogout = async () => {
    try {
      // First, mark session end to ensure proper session tracking
      try {
        await fetch('/api/session/end', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('✅ Session end marked before logout');
      } catch (sessionError) {
        console.warn('⚠️ Failed to mark session end before logout:', sessionError);
        // Continue with logout even if session end fails
      }
      
      clearUserData(); // Clear persisted user data
      if (logoutAuth0) {
        logoutAuth0(); // Use the logout function from auth store (includes master loading reset)
      } else {
        console.warn('No logout function available from auth store');
      }
    } catch (error) {
      console.error('Logout process failed:', error);
      // Still attempt logout even if session end fails
      clearUserData();
      if (logoutAuth0) {
        logoutAuth0();
      }
    }
  };

  // Get user name with fallback logic - prioritize first name only
  const getUserName = (): string => {
    // PRIORITY 1: Use billing_name from database (set during checkout)
    // Extract first name only for cleaner display
    if (userBillingName) {
      // Check if billing_name is actually an email (for trial users)
      if (userBillingName.includes('@')) {
        const emailUsername = userBillingName.split('@')[0];
        // Clean up email username (remove + suffixes, replace dots/underscores)
        const cleanUsername = emailUsername
          .split('+')[0] // Remove +suffix
          .replace(/[._-]/g, ' ') // Replace separators with spaces
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Title case
          .join(' ');
        return cleanUsername;
      }
      // Otherwise treat as a normal name
      const firstName = userBillingName.split(' ')[0];
      return firstName;
    }
    
    // PRIORITY 2: Try Auth0 given_name (first name)
    if (user?.given_name) return user.given_name;
    
    // PRIORITY 3: Try to extract first name from Auth0 name field
    if (user?.name && !user.name.includes('@')) {
      // If name doesn't look like an email, use first part
      const firstName = user.name.split(' ')[0];
      return firstName;
    }
    
    // PRIORITY 4: Try nickname
    if (user?.nickname && !user.nickname.includes('@')) {
      return user.nickname;
    }
    
    // PRIORITY 5: Extract name from email (before @)
    if (user?.email) {
      const emailUsername = user.email.split('@')[0];
      // Clean up email username (remove + suffixes, replace dots/underscores)
      const cleanUsername = emailUsername
        .split('+')[0] // Remove +suffix
        .replace(/[._-]/g, ' ') // Replace separators with spaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Title case
        .join(' ');
      return cleanUsername;
    }
    
    // Final fallback
    return 'User';
  };

    return (
        <motion.nav
            className={`bg-black/20 backdrop-blur-lg border-b border-white/10 text-white pl-4 pr-4 py-3 flex items-center justify-between shadow-2xl sticky top-0 z-40 touch-none select-none min-h-[64px] ${hasAnimated ? 'layout-persistent' : ''}`}
            initial={hasAnimated ? false : { y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            onAnimationComplete={() => {
              if (!hasAnimated) markComplete();
            }}
            style={{ touchAction: 'none' }}
        >
            {/* Hamburger Button: Remains on the left, visible only on md and smaller screens */}
            <motion.button
                onClick={toggleSidebar}
                className="bg-white/10 backdrop-blur-sm inline-flex items-center justify-center p-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent focus:ring-white/50 cursor-pointer md:hidden transition-all duration-300 ease-in-out border border-white/10 transform hover:scale-105"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <span className="sr-only">Open sidebar</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
            </motion.button>

      {/* Empty div to push user info to the right on md+ screens if hamburger is not shown, or rely on justify-between */}
      {/* For md+ screens, hamburger is hidden. If there's no other element on the left, user info will naturally be on the right due to justify-between. */}
      {/* If there was a desire for a centered element on desktop, this is where it would go. */}
      {/* For now, an explicit empty element isn't strictly needed with justify-between if the hamburger is the only other direct child on mobile. */}
      {/* However, to ensure consistent behavior and spacing if other elements were added, an invisible spacer can be useful. */}
      <div className="hidden md:block md:flex-1"></div> {/* This acts as a spacer on larger screens if needed, or can be removed if hamburger is always the only left item */}

            {/* User Info & Logout: Remains on the right */}
            <div className="flex items-center space-x-4">
                <motion.div 
                    className="user-info flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10"
                    initial={hasAnimated ? false : { opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: hasAnimated ? 0 : 0.2, duration: hasAnimated ? 0 : 0.3 }}
                >
                    {user?.picture && (
                        <motion.img
                            src={user.picture}
                            alt="Profile"
                            className="w-9 h-9 rounded-full border-2 border-white/20 shadow-lg"
                            referrerPolicy="no-referrer"
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ duration: 0.3, ease: "easeOut", type: 'spring', stiffness: 400 }}
                        />
                    )}
                    {/* Display name and plan */}
                    <div className="flex flex-col items-end">
                        <span className="username text-sm text-white/90 font-medium">
                            Welcome, {getUserName()}
                        </span>
                        {plan && (
                            <motion.span 
                                className="user-plan text-xs font-medium text-emerald-300/80 tracking-wide"
                                initial={hasAnimated ? false : { scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: hasAnimated ? 0 : 0.5, duration: hasAnimated ? 0 : 0.3, type: 'spring', stiffness: 300 }}
                            >
                                {plan} Plan
                            </motion.span>
                        )}
                    </div>
                </motion.div>

                {/* Notification Bell */}
                <motion.div
                    className="z-[55]"
                    initial={hasAnimated ? false : { opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: hasAnimated ? 0 : 0.3, duration: hasAnimated ? 0 : 0.3 }}
                >
                    <NotificationBell />
                </motion.div>

                {/* Logout Icon Button with Tooltip */}
                <div className="relative group z-[60]">
                    <motion.button
                        className="bg-white/10 backdrop-blur-sm p-2.5 rounded-xl text-white/70 hover:text-red-400 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-red-400/50 transition-all duration-300 ease-in-out cursor-pointer border border-white/10 transform hover:scale-105"
                        onClick={handleLogout}
                        aria-label="Log Out"
                        whileHover={{ scale: 1.1, rotate: 15 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    </motion.button>
                    {/* Tooltip */}
                    <motion.span 
                        className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 hidden group-hover:block px-3 py-1.5 bg-black/95 backdrop-blur-sm text-white text-xs rounded-lg shadow-2xl whitespace-nowrap z-[9999] border border-white/30 pointer-events-none"
                        initial={hasAnimated ? false : { opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: hasAnimated ? 0 : 0.2 }}
                    >
                        Log Out
                    </motion.span>
                </div>
            </div>
        </motion.nav>
    );
};

export default React.memo(Navbar);